import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import rankingComercialService from 'features/comercial/services/rankingComercialService';
import { chaveDoVendedor } from 'features/comercial/cenas/eventosDoRanking';
import { getCompanyContext } from 'utils/setCompanyContext';

const normalizarVendedor = (vendedor, empresa, index) => {
  const nome = vendedor?.nome || vendedor?.vendedor || 'Vendedor não informado';

  const contratos = Number(vendedor?.contratos ?? vendedor?.total_contratos ?? 0);
  const metaVendas = vendedor?.meta_vendas;
  const metaNumerica = metaVendas !== undefined && metaVendas !== null ? Number(metaVendas) : null;

  return {
    id: vendedor?.id || vendedor?.id_vendedor || vendedor?.vendedor_id || `${nome}-${empresa}-${index}`,
    nome,
    contratos,
    meta_vendas: metaNumerica,
    meta_periodo: vendedor?.meta_periodo || null,
    percentual_meta: metaNumerica > 0 ? Number(vendedor?.percentual_meta ?? ((contratos / metaNumerica) * 100)) : null,
    saldo_meta: metaNumerica !== null ? Number(vendedor?.saldo_meta ?? (metaNumerica - contratos)) : null,
    tem_meta: Boolean(vendedor?.tem_meta || metaNumerica !== null),
    // Campos aditivos do regime da régua. Este normalizador RECONSTRÓI o objeto a partir de uma
    // whitelist, então campo novo do backend some antes de chegar à tela se não for listado aqui.
    fonte_meta: vendedor?.fonte_meta || null,
    fora_da_equipe: Boolean(vendedor?.fora_da_equipe),
    tipo_participante: vendedor?.tipo_participante || null,
    foto_url: vendedor?.foto_url || null,
    foto_pontos: vendedor?.foto_pontos || null,
    tem_foto: Boolean(vendedor?.tem_foto || vendedor?.foto_url),
    empresas: Array.isArray(vendedor?.empresas) && vendedor.empresas.length > 0
      ? vendedor.empresas
      : [empresa],
  };
};

const consolidarRankings = (resultados) => {
  const porNome = new Map();
  const metasDiarias = new Map();

  resultados.forEach((resultado) => {
    if (!resultado.success || !Array.isArray(resultado.data?.ranking_vendedores)) return;

    resultado.data.ranking_vendedores.forEach((vendedor, index) => {
      const normalizado = normalizarVendedor(vendedor, resultado.companyName, index);
      const chave = normalizado.nome.trim().toLocaleLowerCase('pt-BR');
      const atual = porNome.get(chave);

      if (atual) {
        atual.contratos += normalizado.contratos;
        if (normalizado.tem_meta) {
          atual.meta_vendas = Number(atual.meta_vendas || 0) + Number(normalizado.meta_vendas || 0);
          atual.tem_meta = true;
          atual.saldo_meta = atual.meta_vendas - atual.contratos;
          atual.percentual_meta = atual.meta_vendas > 0 ? (atual.contratos / atual.meta_vendas) * 100 : 0;
        }
        // Fora da equipe só se estiver fora em TODAS as empresas do consolidado.
        atual.fora_da_equipe = atual.fora_da_equipe && normalizado.fora_da_equipe;
        atual.tipo_participante = atual.tipo_participante || normalizado.tipo_participante;
        if (normalizado.fonte_meta && atual.fonte_meta && atual.fonte_meta !== normalizado.fonte_meta) {
          atual.fonte_meta = 'misto';
        } else {
          atual.fonte_meta = atual.fonte_meta || normalizado.fonte_meta;
        }
        atual.empresas = Array.from(new Set([...atual.empresas, ...normalizado.empresas]));
        if (!atual.foto_url && normalizado.foto_url) {
          atual.foto_url = normalizado.foto_url;
          // Os pontos marcam o rosto DESTA foto: viajam com ela, nunca com a de outra empresa.
          atual.foto_pontos = normalizado.foto_pontos;
          atual.tem_foto = true;
        }
      } else {
        porNome.set(chave, normalizado);
      }
    });

    (resultado.data.meta_diaria_interna?.vendedores || []).forEach((vendedor) => {
      const chave = chaveDoVendedor(vendedor.vendedor_nome);
      if (!chave) return;
      const atual = metasDiarias.get(chave) || {
        vendedor_id: vendedor.vendedor_id,
        vendedor_nome: vendedor.vendedor_nome,
        maior_faixa_percentual: 0,
        meta_planejada_hoje: 0,
        meta_necessaria_hoje: 0,
        vendas_hoje: 0,
        faltam_hoje: 0,
        meta_acumulada_esperada: 0,
      };
      atual.maior_faixa_percentual = Math.max(atual.maior_faixa_percentual, Number(vendedor.maior_faixa_percentual || 0));
      ['meta_planejada_hoje', 'meta_necessaria_hoje', 'vendas_hoje', 'faltam_hoje', 'meta_acumulada_esperada']
        .forEach((campo) => { atual[campo] += Number(vendedor[campo] || 0); });
      metasDiarias.set(chave, atual);
    });
  });

  const ranking = Array.from(porNome.values()).sort((a, b) => b.contratos - a.contratos);
  const totalVendasRanking = ranking.reduce((total, vendedor) => total + vendedor.contratos, 0);
  const totalVendasApi = resultados.reduce((total, resultado) => (
    total + Number(resultado.data?.total_vendas || 0)
  ), 0);
  const totalVendas = totalVendasApi > 0 ? totalVendasApi : totalVendasRanking;
  const maiorQuantidade = Math.max(...ranking.map((vendedor) => vendedor.contratos), 0);
  const metaTotal = resultados.reduce((total, resultado) => (
    total + Number(resultado.data?.meta_total || 0)
  ), 0);
  // A supermeta é opcional: soma só o que veio e permanece NULA quando nenhuma empresa do contexto
  // tem uma. Zero significaria alvo já batido.
  const superMetaTotal = resultados.reduce((total, resultado) => {
    const valor = resultado.data?.super_meta_total;
    return valor === null || valor === undefined ? total : Number(total || 0) + Number(valor);
  }, null);

  // As faixas da régua são de UMA empresa. No consolidado, percentual de meta somada não é faixa de
  // ninguém — fica nulo, e o painel comemora só a meta batida.
  const comDados = resultados.filter((resultado) => resultado.success);
  const marcosDeMeta = comDados.length === 1 ? comDados[0].data?.marcos_de_meta || null : null;
  // No consolidado, uma empresa sem leitura do mês invalida a linha de chegada inteira.
  const metaIndisponivel = resultados.some((resultado) => !resultado.success || resultado.data?.meta_diaria_interna?.indisponivel);
  const vendedoresDiarios = metaIndisponivel ? [] : Array.from(metasDiarias.values());
  const metaDiariaInterna = {
    indisponivel: metaIndisponivel,
    data: resultados.find((resultado) => resultado.data?.meta_diaria_interna?.data)?.data.meta_diaria_interna.data || null,
    meta_planejada_total: vendedoresDiarios.reduce((soma, vendedor) => soma + vendedor.meta_planejada_hoje, 0),
    meta_necessaria_total: vendedoresDiarios.reduce((soma, vendedor) => soma + vendedor.meta_necessaria_hoje, 0),
    maior_meta_necessaria: Math.max(...vendedoresDiarios.map((vendedor) => vendedor.meta_necessaria_hoje), 0),
    ...linhaDoDiaConsolidada(metaIndisponivel ? [] : comDados.map((resultado) => resultado.data?.meta_diaria_interna)),
    vendedores: vendedoresDiarios,
  };

  return {
    ranking,
    top3: ranking.slice(0, 3),
    totalVendas,
    totalVendedores: ranking.length,
    maiorQuantidade,
    metaTotal,
    superMetaTotal,
    marcosDeMeta,
    metaDiariaInterna,
  };
};

/**
 * Identidade do recorte de datas de uma carga.
 *
 * A tela compara a chave do que PEDIU com a do que está em memória: enquanto as duas diferem, o
 * ranking exibido é de outro período, e mostrá-lo sob o título novo seria número errado sem erro.
 */
export const chaveDoRecorte = (filtroData) => `${filtroData?.start || ''}|${filtroData?.end || ''}`;

/**
 * A meta e a supermeta do dia de várias empresas numa pista só: o planejado de hoje das equipes,
 * somado, dividido por todas as internas. Média das metas do dia de cada empresa pesaria igual uma
 * equipe de 2 e uma de 6. Empresa sem supermeta entra com a própria meta.
 */
const linhaDoDiaConsolidada = (metasDiarias) => {
  const equipes = metasDiarias.map((meta) => meta?.equipe).filter((equipe) => Number(equipe?.internas) > 0);
  const internas = equipes.reduce((soma, equipe) => soma + Number(equipe.internas), 0);
  if (!internas) return { meta_do_dia: 0, supermeta_do_dia: null };

  const meta = Math.ceil(equipes.reduce((soma, equipe) => soma + Number(equipe.meta_hoje || 0), 0) / internas);
  const supermeta = Math.ceil(equipes.reduce((soma, equipe) => soma + (Number(equipe.supermeta_hoje) || Number(equipe.meta_hoje) || 0), 0) / internas);

  return { meta_do_dia: meta, supermeta_do_dia: supermeta > meta ? supermeta : null };
};

const useComercialRanking = (filtroData = null, companyContextProp = null) => {
  const [ranking, setRanking] = useState([]);
  const [top3, setTop3] = useState([]);
  const [totalVendas, setTotalVendas] = useState(0);
  const [totalVendedores, setTotalVendedores] = useState(0);
  const [maiorQuantidade, setMaiorQuantidade] = useState(0);
  const [metaTotal, setMetaTotal] = useState(0);
  const [superMetaTotal, setSuperMetaTotal] = useState(null);
  const [marcosDeMeta, setMarcosDeMeta] = useState(null);
  const [metaDiariaInterna, setMetaDiariaInterna] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [chaveDosDados, setChaveDosDados] = useState(null);
  // Só a carga mais recente grava estado. Alternar o recorte duas vezes seguidas deixa duas
  // requisições no ar, e a que chega por último não é necessariamente a que foi pedida por último.
  const cargaAtual = useRef(0);

  /**
   * O contexto de empresa por CONTEÚDO, e não por identidade.
   *
   * Quem chama costuma passar `getCompanyContext()`, que devolve um objeto novo a cada chamada (sai
   * de um `JSON.parse`). Depender da identidade fazia cada renderização da página disparar uma
   * consulta — e a página do Comercial renderiza sozinha a cada 8 s, por causa do carrossel de
   * gráficos. O seletor dizia "5 min" e a tela consultava dezenas de vezes nesse tempo.
   */
  const chaveDoContexto = companyContextProp ? JSON.stringify(companyContextProp) : '';
  const contextoEstavel = useMemo(
    () => (chaveDoContexto ? JSON.parse(chaveDoContexto) : null),
    [chaveDoContexto],
  );

  const filtrosKey = filtroData ? `${filtroData.start}-${filtroData.end}-${filtroData.presetId || ''}` : 'default';

  /**
   * Devolve `true` em sucesso e `false` em falha.
   *
   * O retorno existe para o backoff do polling: ele precisa saber o resultado de CADA ciclo, e
   * observar o estado `error` não serve — ele é zerado no início de cada carga, então duas falhas
   * seguidas podem não produzir mudança visível de estado no momento certo.
   */
  const carregarRanking = useCallback(async (options = {}) => {
    const carga = ++cargaAtual.current;
    setLoading(true);
    setError(null);

    try {
      const companyContext = contextoEstavel || getCompanyContext();
      const companies = companyContext?.companies?.length
        ? companyContext.companies
        : [{ name: 'MOV Fibra' }];
      const filtros = filtroData ? {
        data_inicio: filtroData.start,
        data_fim: filtroData.end,
      } : {};
      const resultados = await Promise.all(
        companies.map((company) => rankingComercialService.buscarRankingEmpresa(
          company.name,
          filtros,
          { force: !!options.force },
        ))
      );
      if (carga !== cargaAtual.current) return true;

      const consolidado = consolidarRankings(resultados);

      setRanking(consolidado.ranking);
      setTop3(consolidado.top3);
      setTotalVendas(consolidado.totalVendas);
      setTotalVendedores(consolidado.totalVendedores);
      setMaiorQuantidade(consolidado.maiorQuantidade);
      setMetaTotal(consolidado.metaTotal);
      setSuperMetaTotal(consolidado.superMetaTotal);
      setMarcosDeMeta(consolidado.marcosDeMeta);
      setMetaDiariaInterna(consolidado.metaDiariaInterna);
      setUltimaAtualizacao(new Date());
      setChaveDosDados(chaveDoRecorte(filtroData));

      return true;
    } catch (err) {
      // Carga substituída não conta como falha: o backoff do polling mede a origem, não a troca de recorte.
      if (carga !== cargaAtual.current) return true;

      console.error('Erro ao carregar ranking comercial:', err);
      setError(err.message || 'Não foi possível carregar o ranking comercial.');

      return false;
    } finally {
      if (carga === cargaAtual.current) setLoading(false);
      window.dispatchEvent(new CustomEvent('company-change-complete'));
    }
  }, [contextoEstavel, filtroData]);

  useEffect(() => {
    carregarRanking();

    const handleCompanyChange = () => carregarRanking();
    window.addEventListener('company-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [carregarRanking, filtrosKey]);

  return {
    ranking,
    top3,
    totalVendas,
    totalVendedores,
    maiorQuantidade,
    metaTotal,
    superMetaTotal,
    marcosDeMeta,
    metaDiariaInterna,
    loading,
    error,
    ultimaAtualizacao,
    chaveDosDados,
    recarregar: carregarRanking,
  };
};

export default useComercialRanking;
